import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, RefreshCw } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authAPI } from '../../services/api';

export const EmailVerification: React.FC = () => {
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { userId, email } = location.state || {};

  // Redirect if no state data
  useEffect(() => {
    if (!userId || !email) {
      toast.error('Please start from the signup page');
      navigate('/user/signup');
    }
  }, [userId, email, navigate]);

  // Timer for resend OTP
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value.slice(-1); // Only take the last character
    setOtpCode(newOtpCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtpCode = [...otpCode];
    
    for (let i = 0; i < paste.length; i++) {
      newOtpCode[i] = paste[i];
    }
    
    setOtpCode(newOtpCode);
  };

  const handleVerify = async () => {
    const otp = otpCode.join('');
    
    if (otp.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);

    try {
      const data = await authAPI.verifyEmail(userId, otp);

      if (data.success) {
        toast.success('Email verified successfully!');
        toast.success('Account verified! Please login to continue.');
        navigate('/user/login');
      }

    } catch (error: any) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Failed to verify email');
      
      // Clear OTP on error
      setOtpCode(['', '', '', '', '', '']);
      const firstInput = document.getElementById('otp-0');
      firstInput?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);

    try {
      await authAPI.resendOTP(userId);

      toast.success('New verification code sent to your email!');
      setTimer(60);
      setCanResend(false);
      setOtpCode(['', '', '', '', '', '']);

    } catch (error: any) {
      console.error('Resend error:', error);
      toast.error(error.message || 'Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  };

  if (!userId || !email) {
    return null; // Component will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-[#63B56F] rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify Your Email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a 6-digit code to
            <br />
            <span className="font-medium text-[#63B56F]">{email}</span>
          </p>
        </div>

        <motion.div
          className="mt-8 space-y-6 bg-white rounded-xl shadow-lg p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 text-center mb-4">
              Enter verification code
            </label>
            
            <div className="flex justify-center space-x-3" onPaste={handlePaste}>
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#63B56F] focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:ring-opacity-20 transition-colors"
                  disabled={isLoading}
                />
              ))}
            </div>
          </div>

          <button
            onClick={handleVerify}
            disabled={isLoading || otpCode.join('').length !== 6}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#63B56F] hover:bg-[#4A9B57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#63B56F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Verifying...
              </div>
            ) : (
              <div className="flex items-center">
                Verify Email
                <ArrowRight className="ml-2 h-4 w-4" />
              </div>
            )}
          </button>

          <div className="text-center">
            {!canResend ? (
              <p className="text-sm text-gray-600">
                Didn't receive the code?{' '}
                <span className="text-gray-400">
                  Resend in {timer}s
                </span>
              </p>
            ) : (
              <button
                onClick={handleResendOTP}
                disabled={isResending}
                className="text-sm font-medium text-[#63B56F] hover:text-[#4A9B57] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <div className="flex items-center justify-center">
                    <RefreshCw className="animate-spin h-4 w-4 mr-1" />
                    Sending...
                  </div>
                ) : (
                  'Resend verification code'
                )}
              </button>
            )}
          </div>

          <div className="text-center pt-4 border-t border-gray-200">
            <button
              onClick={() => navigate('/user/signup')}
              className="text-sm text-gray-600 hover:text-[#63B56F]"
            >
              ← Back to signup
            </button>
          </div>
        </motion.div>

        {/* Info Section */}
        <motion.div 
          className="bg-blue-50 rounded-xl p-6 border border-blue-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            💡 Verification Tips
          </h3>
          <ul className="text-xs text-blue-800 space-y-1">
            <li>• Check your spam/junk folder if you don't see the email</li>
            <li>• The code expires in 10 minutes</li>
            <li>• You can request a new code after 60 seconds</li>
          </ul>
        </motion.div>
      </motion.div>
    </div>
  );
};